import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlanHeader from './PlanHeader';
import { v4 as uuidv4 } from 'uuid';

const parsePlan = (rawPlan) => {
  if (!rawPlan) return [];
  
  const headers = [];
  const lines = rawPlan.split('\n').filter(line => line.trim());
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('##') && !trimmedLine.startsWith('###')) {
      // H2 header
      const content = trimmedLine.replace('##', '').trim();
      headers.push({
        id: uuidv4(),
        content,
        level: 2,
        subheaders: []
      });
    } else if (trimmedLine.startsWith('###')) {
      // H3 header - add to last H2's subheaders
      const content = trimmedLine.replace('###', '').trim();
      if (headers.length > 0) {
        headers[headers.length - 1].subheaders.push({
          id: uuidv4(),
          content,
          level: 3
        });
      }
    }
  });
  
  return headers;
};

const serializePlan = (headers) => {
  let plan = '';
  
  headers.forEach(header => {
    plan += `## ${header.content}\n`;
    header.subheaders.forEach(subheader => {
      plan += `### ${subheader.content}\n`;
    });
    plan += '\n';
  });
  
  return plan.trim();
};

const PlanEditor = ({ 
  rawPlan, 
  onChange, 
  onGeneratePlan, 
  isGenerating = false, 
  isShortPost = false 
}) => {
  const [headers, setHeaders] = useState([]);
  const [newItemId, setNewItemId] = useState(null);
  
  useEffect(() => {
    const parsedHeaders = parsePlan(rawPlan);
    // For short posts, only keep the first section
    if (isShortPost && parsedHeaders.length > 1) {
      const newHeaders = [parsedHeaders[0]];
      setHeaders(newHeaders);
      notifyChange(newHeaders);
    } else {
      setHeaders(parsedHeaders);
    }
  }, [rawPlan, isShortPost]);

  const notifyChange = (newHeaders) => {
    const serialized = serializePlan(newHeaders);
    console.log('Notifying change:', serialized); // Debug log
    onChange(serialized);
  };

  const handleHeaderChange = (headerId, newContent) => {
    const newHeaders = headers.map(header => {
      if (header.id === headerId) {
        return { ...header, content: newContent };
      }
      return {
        ...header,
        subheaders: header.subheaders.map(sub => 
          sub.id === headerId ? { ...sub, content: newContent } : sub
        )
      };
    });
    
    setHeaders(newHeaders);
    notifyChange(newHeaders);
    setNewItemId(null); // Clear newItemId after content is changed
  };

  const handleAddHeader = () => {
    // Don't allow adding more sections for short posts if one already exists
    if (isShortPost && headers.length > 0) {
      return;
    }

    const id = uuidv4();
    const newHeader = {
      id,
      content: 'New Section',
      level: 2,
      subheaders: []
    };
    
    const newHeaders = [...headers, newHeader];
    setHeaders(newHeaders);
    notifyChange(newHeaders);
    setNewItemId(id); // Set the new header as active
  };

  const handleAddSubheader = (parentId) => {
    const id = uuidv4();
    const newHeaders = headers.map(header => {
      if (header.id === parentId) {
        return {
          ...header,
          subheaders: [
            ...header.subheaders,
            {
              id,
              content: 'New Subsection',
              level: 3
            }
          ]
        };
      }
      return header;
    });
    
    setHeaders(newHeaders);
    notifyChange(newHeaders);
    setNewItemId(id); // Set the new subheader as active
  };

  const handleDeleteHeader = (headerId) => {
    // First try to delete from subheaders
    let found = false;
    const newHeaders = headers.map(header => {
      const newSubheaders = header.subheaders.filter(sub => sub.id !== headerId);
      if (newSubheaders.length !== header.subheaders.length) {
        found = true;
        return { ...header, subheaders: newSubheaders };
      }
      return header;
    });
    
    // If not found in subheaders, delete main header
    if (!found) {
      const filteredHeaders = newHeaders.filter(h => h.id !== headerId);
      setHeaders(filteredHeaders);
      notifyChange(filteredHeaders);
    } else {
      setHeaders(newHeaders);
      notifyChange(newHeaders);
    }
  };

  const handleMoveHeader = (headerId, direction) => {
    // First try to move in subheaders
    let found = false;
    const newHeaders = headers.map(header => {
      const subIndex = header.subheaders.findIndex(sub => sub.id === headerId);
      if (subIndex !== -1) {
        found = true;
        const newSubheaders = [...header.subheaders];
        const newIndex = subIndex + (direction === 'up' ? -1 : 1);
        if (newIndex >= 0 && newIndex < newSubheaders.length) {
          [newSubheaders[subIndex], newSubheaders[newIndex]] = 
          [newSubheaders[newIndex], newSubheaders[subIndex]];
        }
        return { ...header, subheaders: newSubheaders };
      }
      return header;
    });
    
    // If not found in subheaders, move main header
    if (!found) {
      const index = headers.findIndex(h => h.id === headerId);
      const newIndex = index + (direction === 'up' ? -1 : 1);
      if (newIndex >= 0 && newIndex < headers.length) {
        const reorderedHeaders = [...headers];
        [reorderedHeaders[index], reorderedHeaders[newIndex]] = 
        [reorderedHeaders[newIndex], reorderedHeaders[index]];
        setHeaders(reorderedHeaders);
        notifyChange(reorderedHeaders);
      }
    } else {
      setHeaders(newHeaders);
      notifyChange(newHeaders);
    }
  };

  const handleMoveToHeader = (subheaderId, parentId) => {
    // Find the subheader and its parent
    const parentHeader = headers.find(h => h.id === parentId);
    const subheader = parentHeader?.subheaders.find(sub => sub.id === subheaderId);
    
    if (!subheader) return;

    // Create new header from subheader
    const newHeader = {
      id: subheader.id,
      content: subheader.content,
      level: 2,
      subheaders: []
    };

    // Remove subheader from parent
    const newHeaders = headers.map(header => {
      if (header.id === parentId) {
        return {
          ...header,
          subheaders: header.subheaders.filter(sub => sub.id !== subheaderId)
        };
      }
      return header;
    });

    // Add new header after the parent
    const parentIndex = newHeaders.findIndex(h => h.id === parentId);
    newHeaders.splice(parentIndex + 1, 0, newHeader);
    
    setHeaders(newHeaders);
    notifyChange(newHeaders);
  };

  const handleMoveToSubheader = (headerId, targetHeaderId) => {
    // Find the header to move
    const headerToMove = headers.find(h => h.id === headerId);
    if (!headerToMove) return;

    // Create new subheader from header
    const newSubheader = {
      id: headerToMove.id,
      content: headerToMove.content,
      level: 3
    };

    // Add subheader to target and remove original header
    const newHeaders = headers
      .filter(h => h.id !== headerId)
      .map(header => {
        if (header.id === targetHeaderId) {
          return {
            ...header,
            subheaders: [...header.subheaders, newSubheader]
          };
        }
        return header;
      });

    setHeaders(newHeaders);
    notifyChange(newHeaders);
  };

  return (
    <Card>
      <CardContent sx={{ 
        p: { xs: 0.5, sm: 2 }, 
        '&:last-child': { pb: { xs: 0.5, sm: 2 } } 
      }}>
        {headers.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            py: { xs: 1.5, sm: 2 },
            px: { xs: 1, sm: 2 },
            backgroundColor: 'action.hover',
            borderRadius: 1
          }}>
            <Typography 
              color="text.secondary" 
              sx={{ 
                mb: { xs: 0.5, sm: 1 }, 
                fontSize: { xs: '0.813rem', sm: '0.875rem' },
                textAlign: 'center',
                px: 1
              }}
            >
              Generate a plan to get started
            </Typography>
            <Button
              onClick={() => onGeneratePlan?.()}
              variant="contained"
              size="small"
              disabled={isGenerating}
              startIcon={headers.length > 0 ? <RefreshIcon /> : null}
              sx={{
                minWidth: { sm: '150px' },
                fontSize: { xs: '0.813rem', sm: 'inherit' },
                py: { xs: 0.5, sm: 1 }
              }}
            >
              {isGenerating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Generating...</span>
                </Box>
              ) : headers.length > 0 ? (
                'Regenerate Plan'
              ) : (
                'Generate Plan'
              )}
            </Button>
          </Box>
        ) : (
          <>
            <List sx={{ 
              p: 0,
              '& > .MuiListItem-root': { 
                display: 'block',
                p: 0,
                mb: { xs: 0.5, sm: 1 }
              }
            }}>
              {headers.map((header, index) => (
                <ListItem key={header.id}>
                  <PlanHeader
                    header={header}
                    index={index}
                    onDelete={() => handleDeleteHeader(header.id)}
                    onChange={(newContent) => handleHeaderChange(header.id, newContent)}
                    onAddSubheader={() => handleAddSubheader(header.id)}
                    onMoveUp={() => handleMoveHeader(header.id, 'up')}
                    onMoveDown={() => handleMoveHeader(header.id, 'down')}
                    onMoveToSubheader={() => {
                      const targetIndex = index > 0 ? index - 1 : 1;
                      if (targetIndex < headers.length) {
                        handleMoveToSubheader(header.id, headers[targetIndex].id);
                      }
                    }}
                    canMoveUp={index > 0}
                    canMoveDown={index < headers.length - 1}
                    canMoveToSubheader={headers.length > 1}
                    isSubheader={false}
                    initialEditing={header.id === newItemId}
                  />
                  
                  <List sx={{ 
                    pl: { xs: 2, sm: 3 },
                    '& > .MuiListItem-root': { 
                      display: 'block',
                      p: 0,
                      mb: { xs: 0.25, sm: 0.5 }
                    }
                  }}>
                    {header.subheaders.map((subheader, subIndex) => (
                      <ListItem key={subheader.id}>
                        <PlanHeader
                          header={subheader}
                          index={subIndex}
                          onDelete={() => handleDeleteHeader(subheader.id)}
                          onChange={(newContent) => handleHeaderChange(subheader.id, newContent)}
                          onMoveUp={() => handleMoveHeader(subheader.id, 'up')}
                          onMoveDown={() => handleMoveHeader(subheader.id, 'down')}
                          onMoveToHeader={() => handleMoveToHeader(subheader.id, header.id)}
                          canMoveUp={subIndex > 0}
                          canMoveDown={subIndex < header.subheaders.length - 1}
                          canMoveToHeader={true}
                          isSubheader={true}
                          initialEditing={subheader.id === newItemId}
                        />
                      </ListItem>
                    ))}
                    <ListItem>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddSubheader(header.id)}
                        variant="text"
                        size="small"
                        sx={{ 
                          ml: { xs: 2, sm: 3 },
                          color: 'text.secondary',
                          py: { xs: 0.25, sm: 0.5 },
                          minHeight: 0,
                          fontSize: { xs: '0.813rem', sm: 'inherit' },
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        Add Subsection
                      </Button>
                    </ListItem>
                  </List>
                </ListItem>
              ))}
            </List>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddHeader}
              variant="text"
              size="small"
              sx={{ 
                ml: { xs: 0.5, sm: 1 },
                mt: { xs: 0.5, sm: 1 },
                color: 'text.secondary',
                py: { xs: 0.25, sm: 0.5 },
                minHeight: 0,
                fontSize: { xs: '0.813rem', sm: 'inherit' },
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              disabled={isShortPost && headers.length > 0}
            >
              Add Section
            </Button>
            {isShortPost && headers.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  mt: 1,
                  ml: { xs: 0.5, sm: 1 },
                  fontStyle: 'italic'
                }}
              >
                Short posts are limited to one section
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PlanEditor; 